import React, { useEffect, useRef, useState } from "react";
import { Slider, InputNumber } from "antd";
import InfoIcon from "../InfoIcon";
import { typography, textColors, surfaces, borders, interactive } from "../../../design-system/tokens";

export const SliderField = ({ value, onChange, min, max, label, help, step, infoText, suggestions, unlimitedLabel }) => {
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const updateWidth = (next) => {
      const rounded = Number.isFinite(next) ? Math.round(next) : 0;
      setContainerWidth((prev) => (prev === rounded ? prev : rounded));
    };

    try {
      updateWidth(el.getBoundingClientRect().width);
    } catch (_) {}

    if (typeof ResizeObserver === "undefined") return;

    const obs = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (rect) updateWidth(rect.width);
    });
    obs.observe(el);
    return () => {
      try { obs.disconnect(); } catch (_) {}
    };
  }, []);

  const isCompact = containerWidth > 0 && containerWidth < 520;
  const rowClasses = isCompact
    ? "flex flex-col items-stretch"
    : "flex items-center gap-2";
  const inputRowClasses = isCompact
    ? "flex w-full items-center gap-2"
    : "flex w-full items-center gap-3";

  const supportsUnlimited = typeof unlimitedLabel === "string" && unlimitedLabel.trim().length > 0;
  const isUnlimited = supportsUnlimited && value === null;

  const fallbackMin = typeof min === "number" ? min : 0;
  const sliderValue = isUnlimited ? fallbackMin : (typeof value === "number" ? value : fallbackMin);
  const inputValue = isUnlimited ? null : value;

  return (
    <div ref={containerRef} className="pr-4 w-full">
      {/* Row 1: label + slider + value input */}
      <div className={rowClasses}>
        {label && (
          <span className={isCompact ? `block mb-2 ${typography.label} ${textColors.secondary}` : `${typography.label} ${textColors.secondary} whitespace-nowrap`}>
            {label}
            {infoText ? <span className="ml-2"><InfoIcon text={infoText} /></span> : null}
          </span>
        )}
        <div className={isCompact ? "w-full" : "flex-1 min-w-0"}>
          <div className={inputRowClasses}>
            <div className="flex-1 min-w-0">
              <Slider
                value={sliderValue}
                onChange={onChange}
                min={min}
                max={max}
                step={step}
                disabled={isUnlimited}
              />
            </div>
            <div className="w-24 flex-shrink-0">
              <InputNumber
                value={inputValue}
                min={min}
                max={max}
                step={step}
                className="w-full"
                placeholder={isUnlimited ? "∞" : undefined}
                onChange={(num) => {
                  if (supportsUnlimited && (num === null || num === undefined)) {
                    onChange(null);
                    return;
                  }
                  const next = typeof num === "number" ? num : fallbackMin;
                  if (typeof min === "number" && next < min) {
                    onChange(min);
                    return;
                  }
                  if (typeof max === "number" && next > max) {
                    onChange(max);
                    return;
                  }
                  onChange(next);
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Row 2: quick presets */}
      {(supportsUnlimited || (suggestions && suggestions.length > 0)) && (
        <div className="mt-3 space-y-1.5">
          <div className={`${textColors.muted} ${typography.caption}`}>Quick presets</div>
          <div className="flex gap-1.5 flex-wrap">
            {supportsUnlimited && (
              <button
                key="__unlimited__"
                type="button"
                onClick={() => onChange(null)}
                className={`rounded-lg px-3 py-1.5 transition-all text-xs font-medium border ${
                  isUnlimited
                    ? `${interactive.selectedBg} text-white ${interactive.selectedBorder} shadow-sm`
                    : `${borders.default} ${surfaces.tertiary} ${textColors.primary} ${surfaces.hover}`
                }`}
                aria-pressed={isUnlimited}
                title={unlimitedLabel}
              >
                {unlimitedLabel}
              </button>
            )}
            {(suggestions || []).map((preset) => {
              const isSelected = typeof value === "number" && Number(value) === Number(preset);
              return (
                <button
                  key={preset}
                  type="button"
                  onClick={() => onChange(preset)}
                  className={`rounded-lg px-3 py-1.5 transition-all text-xs font-medium border ${
                    isSelected
                      ? `${interactive.selectedBg} text-white ${interactive.selectedBorder} shadow-sm`
                      : `${borders.default} ${surfaces.tertiary} ${textColors.primary} ${surfaces.hover}`
                  }`}
                  aria-pressed={isSelected}
                  title={`${preset}`}
                >
                  {preset}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {help && <div className={`mt-2 ${typography.caption} ${textColors.tertiary}`}>{help}</div>}
    </div>
  );
};
